import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, currentTenant } from '../../lib/api';
import { Badge, EmptyState, ErrorState, PageHead, Stat } from '../../components/ui';
import { Icon } from '../../components/Icon';

interface ResourceDef {
  key: string;
  label: string;
  description: string;
  columns: Array<{ key: string; label: string; kind: string }>;
  importable: boolean;
  importColumns?: Array<{ key: string; label: string; kind: string; required: boolean; example: string }>;
}

interface Issue {
  row: number;
  column?: string;
  message: string;
}

interface Preview {
  resource: string;
  total: number;
  valid: number;
  create: number;
  duplicate: number;
  errors: Issue[];
  sample: Array<Record<string, unknown>>;
}

/**
 * تنزيل ملف من الخادم.
 *
 * الرابط المباشر لا يصلح: الطلب يحتاج ترويسة المستأجر ورمز الوصول، وهما لا
 * يُرسلان مع تنقّل المتصفح العادي. فيُجلب المحتوى ثم يُحفظ من الذاكرة.
 */
async function download(path: string, filename: string): Promise<void> {
  const blob = await api<Blob>(path, { raw: true });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function DataMove() {
  const [tab, setTab] = useState<'export' | 'import'>('export');

  const catalog = useQuery({
    queryKey: ['data', 'catalog'],
    queryFn: () => api<{ resources: ResourceDef[] }>('/data/catalog'),
  });

  if (catalog.isError) {
    return <ErrorState error={catalog.error} onRetry={() => catalog.refetch()} />;
  }

  const resources = catalog.data?.resources ?? [];

  return (
    <div className="page">
      <PageHead title="تصدير البيانات واستيرادها" subtitle="ملفات CSV تفتح في إكسل" />

      <div className="toolbar">
        <button
          className={`btn${tab === 'export' ? ' btn-primary' : ''}`}
          onClick={() => setTab('export')}
        >
          تصدير
        </button>
        <button
          className={`btn${tab === 'import' ? ' btn-primary' : ''}`}
          onClick={() => setTab('import')}
        >
          استيراد
        </button>
      </div>

      {tab === 'export' ? (
        <ExportTab resources={resources} />
      ) : (
        <ImportTab resources={resources.filter((r) => r.importable)} />
      )}
    </div>
  );
}

/* ==========================================================================
   التصدير
   ========================================================================== */

function ExportTab({ resources }: { resources: ResourceDef[] }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (r: ResourceDef) => {
    setBusy(r.key);
    setError(null);
    try {
      await download(`/data/export/${r.key}`, `${r.label}-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'تعذّر التصدير.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      {error && <div className="alert alert-danger mb-4">{error}</div>}

      <div className="card">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>البيانات</th>
                <th>الأعمدة</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {resources.map((r) => (
                <tr key={r.key}>
                  <td>
                    <span className="cell-strong">{r.label}</span>
                    <div className="tiny mute">{r.description}</div>
                  </td>
                  <td className="cell-mute tiny">
                    {r.columns.slice(0, 5).map((c) => c.label).join('، ')}
                    {r.columns.length > 5 && ` و${r.columns.length - 5} غيرها`}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm"
                      onClick={() => run(r)}
                      disabled={busy === r.key}
                    >
                      {busy === r.key ? 'جارٍ' : 'تنزيل CSV'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {resources.length === 0 && (
          <EmptyState title="لا بيانات متاحة" text="صلاحيتك لا تشمل تصدير بيانات." />
        )}
      </div>

      {/*
        علامة BOM في أول الملف هي ما يجعل إكسل يقرأ العربية.
        بدونها يفتحه بترميز النظام فتظهر طلاسم، فيظن المستخدم التصدير معطوبًا.
      */}
      <p className="tiny mute mt-3">
        الملفات بترميز UTF-8 مع علامة تُمكّن إكسل من قراءة العربية مباشرةً. يُصدَّر حتى عشرة
        آلاف صف في الملف الواحد.
      </p>
    </>
  );
}

/* ==========================================================================
   الاستيراد
   ========================================================================== */

function ImportTab({ resources }: { resources: ResourceDef[] }) {
  const [key, setKey] = useState(resources[0]?.key ?? '');
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [done, setDone] = useState<Record<string, unknown> | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const resource = resources.find((r) => r.key === key);

  const check = useMutation({
    mutationFn: () =>
      api<Preview>(`/data/import/${key}/preview`, { method: 'POST', body: { csv } }),
    onSuccess: (p) => {
      setPreview(p);
      setDone(null);
    },
  });

  const commit = useMutation({
    mutationFn: () => api<Record<string, unknown>>(`/data/import/${key}`, { method: 'POST', body: { csv } }),
    onSuccess: (r) => {
      setDone(r);
      setPreview(null);
      setCsv('');
      setFileName('');
      if (fileRef.current) fileRef.current.value = '';
    },
  });

  const onFile = (file: File) => {
    setFileName(file.name);
    setPreview(null);
    setDone(null);
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result ?? ''));
    // UTF-8 صراحةً: القراءة بترميز النظام تُفسد العربية قبل أن تصل الخادم.
    reader.readAsText(file, 'utf-8');
  };

  const reset = () => {
    setCsv('');
    setFileName('');
    setPreview(null);
    setDone(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <>
      <div className="card">
        <div className="card-head">
          <h2 className="card-title">اختر البيانات والملف</h2>
        </div>
        <div className="card-body">
          <div className="form-grid">
            <div className="field span-2">
              <label className="label" htmlFor="d-resource">
                ما تستورده
              </label>
              <select
                id="d-resource"
                className="select"
                value={key}
                onChange={(e) => {
                  setKey(e.target.value);
                  reset();
                }}
              >
                {resources.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field span-2">
              <span className="label">الأعمدة المقبولة</span>
              <div className="tiny mute">
                {resource?.importColumns?.map((c) => (
                  <span key={c.key}>
                    {c.label}
                    {c.required && <span className="neg"> *</span>}
                    {' · '}
                  </span>
                ))}
              </div>
              <span className="tiny mute mt-2">
                المعلَّم بنجمة مطلوب. الأعمدة تُطابَق بالعنوان لا بالترتيب، فرتّبها كما شئت
                واحذف ما لا تحتاجه.
              </span>
            </div>
          </div>

          <div className="row mt-4" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button
              className="btn"
              onClick={() =>
                void download(`/data/template/${key}`, `${resource?.label ?? key}-قالب.csv`)
              }
            >
              تنزيل القالب
            </button>

            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="input"
              style={{ maxWidth: 260 }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onFile(f);
              }}
            />

            <button
              className="btn btn-primary"
              onClick={() => check.mutate()}
              disabled={!csv || check.isPending}
            >
              {check.isPending ? 'جارٍ الفحص' : 'فحص الملف'}
            </button>
          </div>

          {fileName && (
            <p className="tiny mute mt-2">
              الملف: <span className="num">{fileName}</span>
            </p>
          )}

          {check.isError && (
            <div className="alert alert-danger mt-4">
              {check.error instanceof Error ? check.error.message : 'تعذّر الفحص.'}
            </div>
          )}
        </div>
      </div>

      {done && (
        <div className="alert alert-info mt-4">
          <Icon name="check" size={15} />
          <div>
            أُضيف {String(done.created)} سجلًا
            {Number(done.skipped) > 0 && `، وتُخطّي ${String(done.skipped)} موجودًا من قبل`}
            {Number(done.failed) > 0 && `، وتعذّر ${String(done.failed)}`}.
          </div>
        </div>
      )}

      {preview && (
        <>
          <div className="stats mt-4">
            <Stat label="صفوف الملف" value={preview.total} />
            <Stat label="صالحة" value={preview.valid} />
            <Stat label="ستُضاف" value={<span className="strong">{preview.create}</span>} />
            <Stat label="موجودة من قبل" value={preview.duplicate} />
            <Stat
              label="بها أخطاء"
              value={<span className={preview.errors.length ? 'neg' : ''}>{preview.errors.length}</span>}
            />
          </div>

          {/*
            المعاينة قبل الكتابة: الاستيراد الأعمى يُنشئ ألف سجل خطأ لا سبيل
            لتمييزها عمّا كان في النظام قبله.
          */}
          {preview.errors.length > 0 && (
            <div className="card mt-4">
              <div className="card-head">
                <h2 className="card-title">أخطاء في الملف</h2>
              </div>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>السطر</th>
                      <th>العمود</th>
                      <th>الخطأ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.errors.map((e, i) => (
                      <tr key={i}>
                        <td className="num cell-strong">{e.row}</td>
                        <td className="cell-mute">{e.column ?? '—'}</td>
                        <td className="neg">{e.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="card-body">
                <p className="tiny mute">
                  الصفوف التي بها أخطاء تُتخطّى ولا توقف الباقي. صحّحها في الملف وأعد الفحص إن
                  أردت إدخالها.
                </p>
              </div>
            </div>
          )}

          {preview.sample.length > 0 && (
            <div className="card mt-4">
              <div className="card-head">
                <h2 className="card-title">عيّنة مما سيُضاف</h2>
              </div>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      {Object.keys(preview.sample[0]!).map((h) => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample.map((row, i) => (
                      <tr key={i}>
                        {Object.keys(preview.sample[0]!).map((h) => (
                          <td key={h} className="tiny">
                            {String(row[h] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="row mt-4" style={{ gap: 8 }}>
            <button className="btn" onClick={reset}>
              إلغاء
            </button>
            <button
              className="btn btn-primary"
              onClick={() => commit.mutate()}
              disabled={preview.create === 0 || commit.isPending}
            >
              {commit.isPending
                ? 'جارٍ الإضافة'
                : `تأكيد الاستيراد — ${preview.create} سجلًا`}
            </button>
          </div>

          {commit.isError && (
            <div className="alert alert-danger mt-4">
              {commit.error instanceof Error ? commit.error.message : 'تعذّر الاستيراد.'}
            </div>
          )}
        </>
      )}

      <p className="tiny mute mt-4">
        الاستيراد يُضيف ولا يُعدّل الموجود: السجل الذي يطابق سجلًا قائمًا يُتخطّى. احفظ الملف
        من إكسل بصيغة CSV UTF-8.
      </p>
    </>
  );
}
